import { useContext, useState, useEffect } from 'react';
import { useParams, RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { getAdminMagicSecret } from '../../connector/auth';
import { RestUser, RestApiServer, RestFeedCreatorResult, RestRecentUser } from '../../schema-copy/api-schema';
import { RestAdminAccountResult, RestAccountSummary, AccountStatus, Jurisdiction } from '../../schema-copy/auth-schema';
import { TextButton, DropDownSelector, DropDownSelectorValue, Input, PasswordInput } from 't-lib-ui/widgets';
import { searchIcon, saveIcon, tagsIcon, personAddIcon } from '../../components/icons';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { lookupAccountByEmail, lookupAccountById, getUserByAccountId, adminCreateAccount, adminCreateUser, adminUpdateUser, adminUpdateAccount, adminSetUserCapabilities, adminDeleteAccount, adminDeleteUser, adminGetApiServers, adminGetFeedCreator, adminBanAccount, adminUpdateDeletedUser, adminListRecentUsers, adminGetAccountSummaries } from '../../connector/api';
import { nonNull } from 't-lib-ui/helpers';
import { getUserType, UserType, isOwner, createCapabilities } from '../../schema-copy/capabilities';
import { UserPlan } from '../../schema-copy/userPlans';

const AdminViewUserPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  // Search state
  const [searchQuery, setSearchQuery] = useState(id || '');

  // Data state
  const [account, setAccount] = useState<RestAdminAccountResult | null>(null);
  const [user, setUser] = useState<RestUser | null>(null);
  // API host of the instance holding the currently-loaded user (EU users live only on e1). All
  // api-host mutations for this user (update / capabilities / delete) must target it, not the
  // default host. undefined = default/this server.
  const [userApiHost, setUserApiHost] = useState<string | undefined>(undefined);
  const [userStatus, setUserStatus] = useState<'active' | 'suspended' | 'deleted'>('active');
  const [nickName, setNickName] = useState('');
  const [userPlan, setUserPlan] = useState<UserPlan>('free');
  const [userType, setUserType] = useState<'user' | 'super'>('user');
  const [accountStatus, setAccountStatus] = useState<AccountStatus>('confirmed');
  const [capabilitiesUserType, setCapabilitiesUserType] = useState<UserType>(UserType.User);
  const [confirmElevation, setConfirmElevation] = useState<{ userType: UserType; label: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
  const [deletedMessage, setDeletedMessage] = useState<string | undefined>(undefined);

  // Full user delete (admin equivalent of the Account page self-delete). Separate state from the
  // orphaned-auth-account delete above, which only removes an account with no Downread user.
  const [confirmUserDelete, setConfirmUserDelete] = useState(false);
  const [userDeleteBusy, setUserDeleteBusy] = useState(false);
  const [userDeleteError, setUserDeleteError] = useState<string | undefined>(undefined);

  // Ban / unban this identity (RETENTION-CONCEPT §5). A ban suspends the account, ends its session,
  // and writes a permanent deletedUsers tombstone so the email can't re-register.
  const [confirmBan, setConfirmBan] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banBusy, setBanBusy] = useState(false);
  const [banError, setBanError] = useState<string | undefined>(undefined);

  // Feed → creator lookup. Public feeds no longer store a creatorId; the creator link lives in a
  // user's createdFeeds on that user's own API instance, so the admin picks which server to ask.
  const [feedCreatorQuery, setFeedCreatorQuery] = useState('');
  const [apiServers, setApiServers] = useState<RestApiServer[]>([]);
  const [selectedServerUrl, setSelectedServerUrl] = useState(''); // '' = this (default) server
  const [creatorResult, setCreatorResult] = useState<RestFeedCreatorResult | null>(null);
  const [creatorLoading, setCreatorLoading] = useState(false);
  const [creatorError, setCreatorError] = useState<string | undefined>(undefined);

  // Recent-users list: the last N created users on a chosen API instance, for spotting untagged
  // test accounts to delete. createdAt + tag come from the API user; admin flag + last login are
  // annotated per-account from the auth service (recentSummaries), keyed by accountId.
  const [recentUsers, setRecentUsers] = useState<RestRecentUser[] | null>(null);
  const [recentSummaries, setRecentSummaries] = useState<Record<string, RestAccountSummary>>({});
  // True only once the auth annotation succeeded, so a missing summary reliably means "orphaned"
  // (API user with no auth account) rather than "still loading" / "annotation failed".
  const [recentSummariesReady, setRecentSummariesReady] = useState(false);
  const [recentServerUrl, setRecentServerUrl] = useState(''); // '' = this (default) server
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState<string | undefined>(undefined);
  // Inline confirm + busy/error state for deleting an orphaned user straight from the recent list.
  const [confirmOrphanId, setConfirmOrphanId] = useState<string | undefined>(undefined);
  const [orphanBusyId, setOrphanBusyId] = useState<string | undefined>(undefined);
  const [recentActionError, setRecentActionError] = useState<string | undefined>(undefined);

  // Create-new-user form. Admins create a confirmed account (email + password + jurisdiction); the
  // matching Downread user is then created on the jurisdiction's API instance, and the result is
  // shown below so privileges/plan/status can be edited right away.
  const [showCreate, setShowCreate] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createJurisdiction, setCreateJurisdiction] = useState<Jurisdiction>('US');
  const [createTag, setCreateTag] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>(undefined);

  const magic = getAdminMagicSecret() || '';
  const adminIsOwner = isOwner((appState.state.authData as any)?.capabilities);

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminViewUserPage', {
      'Escape': () => { goTo(routerCtx, '/a/admin/users'); return true; },
    }, false, 0);

    return () => unregisterKeyHandler('adminViewUserPage');
  }, [routerCtx]);

  // Load user if ID provided in URL
  useEffect(() => {
    if (id) {
      handleSearch(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSearch = async (query?: string) => {
    const searchValue = query || searchQuery.trim();
    if (!searchValue) {
      setError('Please enter an email address or account ID');
      return;
    }

    try {
      setLoading(true);
      setError(undefined);
      setAccount(null);
      setUser(null);
      setUserApiHost(undefined);

      // Determine if search is by email or account ID
      const isEmail = searchValue.includes('@');

      let accountData: RestAdminAccountResult;
      if (isEmail) {
        accountData = await lookupAccountByEmail(nonNull(appState.state.authData), searchValue, magic);
      } else {
        accountData = await lookupAccountById(nonNull(appState.state.authData), searchValue, magic);
      }

      setAccount(accountData);
      setUserType(accountData.userType || 'user');
      setAccountStatus(accountData.status);

      // Get membership for Downread
      const downreadMembership = accountData.memberships.find(m => m.appId == 'downread');
      if (!downreadMembership) {
        setError('Account exists in the auth service but has no Downread membership (the user is registered, but no Downread user record was created — or it has been deleted).');
        return;
      }

      // Get user from Downread API. The user record lives on the instance recorded in the
      // membership (EU users are only on e1, not the default api01), so route the lookup there —
      // and remember that host so later mutations (update/capabilities/delete) hit the same server.
      const instanceHost = apiHostForInstance(downreadMembership.instance);
      setUserApiHost(instanceHost);
      const userData = await getUserByAccountId(nonNull(appState.state.authData), accountData.id, magic, instanceHost);
      setUser(userData);
      setUserStatus(userData.userStatus);
      setNickName(userData.nickName || '');
      setUserPlan(userData.userPlan);
      setCapabilitiesUserType(getUserType(downreadMembership.capabilities));

    } catch (e) {
      console.error('Failed to search user:', e);
      setError(e instanceof Error ? e.message : 'User not found');
    } finally {
      setLoading(false);
    }
  };

  // Pick the API instance that must serve a given jurisdiction's user record. EU accounts must be
  // created on an EU instance (instance id starts with 'e', e.g. 'e1'); US/default accounts stay on
  // this (default) server. Returns undefined when no matching instance is configured (single-region
  // dev), letting the request fall through to the default API host.
  const apiHostForJurisdiction = (jurisdiction: Jurisdiction): string | undefined => {
    if (jurisdiction != 'EU') return undefined;
    return apiServers.find(s => s.instance.toLowerCase().startsWith('e'))?.url;
  };

  // Map a membership's instance id (e.g. 'e1') to its API server base URL. Used to route the
  // admin user lookup to the server that actually holds the user record. Returns undefined for the
  // default/empty instance or when that instance isn't configured, falling through to the default host.
  const apiHostForInstance = (instance?: string): string | undefined =>
    instance ? apiServers.find(s => s.instance == instance)?.url : undefined;

  const handleCreate = async () => {
    if (creating) return;
    const email = createEmail.trim();
    if (!email || !createPassword) {
      setCreateError('Email and password are required');
      return;
    }

    try {
      setCreating(true);
      setCreateError(undefined);
      setError(undefined);
      setDeletedMessage(undefined);

      // 1) Create the confirmed auth account. 2) Create the Downread user on the jurisdiction's
      // API instance. The auth account jurisdiction is the durable residency decision.
      const tag = createTag.trim() || undefined;
      const instanceHost = apiHostForJurisdiction(createJurisdiction);
      setUserApiHost(instanceHost);
      const accountData = await adminCreateAccount(nonNull(appState.state.authData), email, createPassword, createJurisdiction, magic, tag);
      const userData = await adminCreateUser(nonNull(appState.state.authData), accountData.id, createJurisdiction, magic, instanceHost, tag);

      // The API server registers the membership with the auth service asynchronously (inter-server
      // message). Until that lands the account looks orphaned (an auth account with no API-side
      // profile). Give it a few seconds to propagate before showing the user, so the view reflects a
      // fully-registered account rather than a transient orphan.
      await new Promise(resolve => setTimeout(resolve, 5000));

      // Show the freshly created account + user for editing. Capabilities default to User; a later
      // re-search reflects the registered membership once it has fully propagated.
      setAccount(accountData);
      setUserType(accountData.userType || 'user');
      setAccountStatus(accountData.status);
      setUser(userData);
      setUserStatus(userData.userStatus);
      setNickName(userData.nickName || '');
      setUserPlan(userData.userPlan);
      setCapabilitiesUserType(UserType.User);
      setSearchQuery(accountData.id);

      // Reset and collapse the create form.
      setShowCreate(false);
      setCreateEmail('');
      setCreatePassword('');
      setCreateJurisdiction('US');
      setCreateTag('');
      setDeletedMessage(`Created user ${userData.id} (account ${accountData.id}).`);
    } catch (e) {
      console.error('Failed to create user:', e);
      setCreateError(e instanceof Error ? e.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  // Load the list of API servers once (same on every instance, so the default host is fine).
  useEffect(() => {
    (async () => {
      try {
        const servers = await adminGetApiServers(nonNull(appState.state.authData), magic);
        setApiServers(servers);
      } catch (e) {
        console.error('Failed to load API servers:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magic]);

  const handleFindCreator = async (feedIdArg?: string, serverUrlArg?: string) => {
    const feedId = (feedIdArg ?? feedCreatorQuery).trim();
    if (!feedId) {
      setCreatorError('Please enter a feed ID');
      return;
    }
    setCreatorLoading(true);
    setCreatorError(undefined);
    setCreatorResult(null);
    try {
      const serverUrl = serverUrlArg ?? selectedServerUrl;
      const result = await adminGetFeedCreator(nonNull(appState.state.authData), feedId, magic, serverUrl || undefined);
      setCreatorResult(result);
    } catch (e) {
      console.error('Failed to look up feed creator:', e);
      setCreatorError(e instanceof Error ? e.message : 'Lookup failed');
    } finally {
      setCreatorLoading(false);
    }
  };

  const handleLoadRecent = async () => {
    if (recentLoading) return;
    setRecentLoading(true);
    setRecentError(undefined);
    setRecentActionError(undefined);
    setConfirmOrphanId(undefined);
    setRecentSummariesReady(false);
    setRecentSummaries({});
    try {
      const users = await adminListRecentUsers(nonNull(appState.state.authData), magic, 10, recentServerUrl || undefined);
      setRecentUsers(users);
      // One batched auth lookup annotates all rows with admin flag + last login (keyed by accountId).
      // A row whose accountId is absent from the result is an orphan (its auth account is gone).
      try {
        const summaries = await adminGetAccountSummaries(nonNull(appState.state.authData), users.map(u => u.accountId), magic);
        const byId: Record<string, RestAccountSummary> = {};
        for (const s of summaries) byId[s.accountId] = s;
        setRecentSummaries(byId);
        setRecentSummariesReady(true);
      } catch (e) {
        console.error('Failed to annotate recent users:', e);
        setRecentActionError('Could not load admin / last-login info from the auth service; orphan detection is unavailable.');
      }
    } catch (e) {
      console.error('Failed to load recent users:', e);
      setRecentUsers(null);
      setRecentError(e instanceof Error ? e.message : 'Failed to load recent users');
    } finally {
      setRecentLoading(false);
    }
  };

  // Open a recent-user row in the detail view above (same screen as a search result).
  const handleOpenRecent = (accountId: string) => {
    setSearchQuery(accountId);
    handleSearch(accountId);
  };

  // Delete an orphaned recent-user (an API user whose auth account no longer exists) straight from
  // the list — the normal detail view can't reach it, since looking the account up first fails.
  // adminDeleteUser tears down the user record and is a no-op on the (already-gone) auth account.
  // Targets the same instance the list was loaded from.
  const handleDeleteOrphan = async (userId: string) => {
    if (orphanBusyId) return;
    setOrphanBusyId(userId);
    setRecentActionError(undefined);
    try {
      await adminDeleteUser(nonNull(appState.state.authData), userId, magic, recentServerUrl || undefined);
      setRecentUsers(prev => prev ? prev.filter(u => u.id != userId) : prev);
      setConfirmOrphanId(undefined);
      setDeletedMessage(`Deleted orphaned user ${userId} (no auth account).`);
    } catch (e) {
      console.error('Failed to delete orphaned user:', e);
      setRecentActionError(e instanceof Error ? e.message : 'Failed to delete user');
    } finally {
      setOrphanBusyId(undefined);
    }
  };

  // Prefill + auto-run when arriving from the feed page via ?feedCreator=<id> (uses the default server).
  useEffect(() => {
    const fc = new URLSearchParams(window.location.search).get('feedCreator');
    if (fc) {
      setFeedCreatorQuery(fc);
      handleFindCreator(fc, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (confirmedCapabilities?: UserType) => {
    if (!user || saving) return;

    const effectiveCapabilitiesUserType = confirmedCapabilities ?? capabilitiesUserType;
    const downreadMembership = account?.memberships.find(m => m.appId == 'downread');
    const originalCapabilitiesUserType = downreadMembership ? getUserType(downreadMembership.capabilities) : UserType.User;
    const capabilitiesChanged = effectiveCapabilitiesUserType != originalCapabilitiesUserType;

    // Require confirmation before elevating to super or owner
    if (capabilitiesChanged && !confirmedCapabilities &&
        (effectiveCapabilitiesUserType == UserType.Superuser || effectiveCapabilitiesUserType == UserType.Owner)) {
      const label = effectiveCapabilitiesUserType == UserType.Owner ? 'Owner' : 'Admin';
      setConfirmElevation({ userType: effectiveCapabilitiesUserType, label });
      return;
    }
    if (confirmedCapabilities) setConfirmElevation(null);

    // Also confirm making account a super-user
    const accountSuperElevation = account && account.userType != userType && userType == 'super' && !confirmedCapabilities;
    if (accountSuperElevation) {
      setConfirmElevation({ userType: UserType.Superuser, label: 'Account Super User' });
      return;
    }

    try {
      setSaving(true);
      setSaveError(undefined);

      const promises: Promise<any>[] = [];

      // Downread API updates (status, plan, nickName)
      const userUpdate: Record<string, string> = {};
      if (user.userStatus != userStatus) userUpdate.userStatus = userStatus;
      if (user.userPlan != userPlan) userUpdate.userPlan = userPlan;
      if (user.nickName != nickName) userUpdate.nickName = nickName;
      if (Object.keys(userUpdate).length)
        promises.push(
          adminUpdateUser(nonNull(appState.state.authData), user.id, userUpdate as any, magic, userApiHost)
            .then(u => setUser(u))
        );

      // Auth service updates (userType, accountStatus)
      if (account) {
        const accountUpdate: Record<string, string> = {};
        if (account.userType != userType) accountUpdate.userType = userType;
        if (account.status != accountStatus) accountUpdate.accountStatus = accountStatus;
        if (Object.keys(accountUpdate).length)
          promises.push(
            adminUpdateAccount(nonNull(appState.state.authData), account.id, accountUpdate as any, magic)
              .then(a => setAccount(a))
          );
      }

      // Downread capabilities update (via interserver to auth service)
      if (capabilitiesChanged)
        promises.push(
          adminSetUserCapabilities(nonNull(appState.state.authData), user.id, effectiveCapabilitiesUserType, magic, userApiHost)
            .then(() => {
              // Update the local account membership so hasChanges resets
              setAccount(prev => prev ? {
                ...prev,
                memberships: prev.memberships.map(m =>
                  m.appId == 'downread' ? { ...m, capabilities: createCapabilities(effectiveCapabilitiesUserType) } : m
                )
              } : prev);
            })
        );

      await Promise.all(promises);

    } catch (e) {
      console.error('Failed to update user:', e);
      setSaveError(e instanceof Error ? e.message : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleEnter = () => {
    handleSearch();
  };

  const handleDeleteAuthAccount = async () => {
    if (!account || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(undefined);
    try {
      await adminDeleteAccount(nonNull(appState.state.authData), account.id, magic);
      setDeletedMessage(`Deleted auth account ${account.id}.`);
      setAccount(null);
      setUser(null);
      setConfirmDelete(false);
    } catch (e) {
      console.error('Failed to delete auth account:', e);
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete auth account');
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!user || userDeleteBusy) return;
    setUserDeleteBusy(true);
    setUserDeleteError(undefined);
    try {
      await adminDeleteUser(nonNull(appState.state.authData), user.id, magic, userApiHost);
      setDeletedMessage(`Deleted user ${user.id}${account ? ` and auth account ${account.id}` : ''}.`);
      setAccount(null);
      setUser(null);
      setConfirmUserDelete(false);
    } catch (e) {
      console.error('Failed to delete user:', e);
      setUserDeleteError(e instanceof Error ? e.message : 'Failed to delete user');
    } finally {
      setUserDeleteBusy(false);
    }
  };

  const isBanned = !!account?.banStatus && account.banStatus != 'none';

  const handleBan = async () => {
    if (!account || banBusy) return;
    setBanBusy(true);
    setBanError(undefined);
    try {
      await adminBanAccount(nonNull(appState.state.authData), account.id, banReason.trim() || undefined, magic);
      setConfirmBan(false);
      setBanReason('');
      setDeletedMessage(`Banned account ${account.id}. Account suspended, session ended, re-registration blocked.`);
      await handleSearch(account.id); // refresh: status now suspended, banStatus permanent
    } catch (e) {
      console.error('Failed to ban account:', e);
      setBanError(e instanceof Error ? e.message : 'Failed to ban account');
    } finally {
      setBanBusy(false);
    }
  };

  // Lift reverses the ban: clear the tombstone ban AND reactivate the (suspended) account.
  const handleLiftBan = async () => {
    if (!account || !account.email || banBusy) return;
    setBanBusy(true);
    setBanError(undefined);
    try {
      await adminUpdateDeletedUser(nonNull(appState.state.authData), account.email, 'none', magic);
      await adminUpdateAccount(nonNull(appState.state.authData), account.id, { accountStatus: 'confirmed' }, magic);
      setDeletedMessage(`Lifted ban on account ${account.id}. Account reactivated.`);
      await handleSearch(account.id);
    } catch (e) {
      console.error('Failed to lift ban:', e);
      setBanError(e instanceof Error ? e.message : 'Failed to lift ban');
    } finally {
      setBanBusy(false);
    }
  };

  const statusOptions: DropDownSelectorValue[] = [
    { id: 'active', text: 'Active', searchTextLc: 'active' },
    { id: 'suspended', text: 'Suspended', searchTextLc: 'suspended' },
    { id: 'deleted', text: 'Deleted', searchTextLc: 'deleted' }
  ];

  // 'test' is an admin-only quota-testing plan: hidden from end users, settable here.
  const planOptions: DropDownSelectorValue[] = [
    { id: 'free', text: 'Free', searchTextLc: 'free' },
    { id: 'pro', text: 'Pro', searchTextLc: 'pro' },
    { id: 'vip', text: 'VIP', searchTextLc: 'vip' },
    { id: 'earlyAdopter', text: 'Early Adopter', searchTextLc: 'early adopter' },
    { id: 'test', text: 'Test (quota testing)', searchTextLc: 'test' }
  ];

  const userTypeOptions: DropDownSelectorValue[] = [
    { id: 'user', text: 'User', searchTextLc: 'user' },
    { id: 'super', text: 'Super', searchTextLc: 'super' }
  ];

  const jurisdictionOptions: DropDownSelectorValue[] = [
    { id: 'US', text: 'US', searchTextLc: 'us' },
    { id: 'EU', text: 'EU', searchTextLc: 'eu' }
  ];

  const accountStatusOptions: DropDownSelectorValue[] = [
    { id: 'unconfirmed', text: 'Unconfirmed', searchTextLc: 'unconfirmed' },
    { id: 'confirmed', text: 'Confirmed', searchTextLc: 'confirmed' },
    { id: 'suspended', text: 'Suspended', searchTextLc: 'suspended' },
    { id: 'wasntMe', text: "Wasn't Me", searchTextLc: "wasn't me" },
    { id: 'deleted', text: 'Deleted', searchTextLc: 'deleted' }
  ];

  const capabilitiesOptions: DropDownSelectorValue[] = [
    { id: UserType.User, text: 'User', searchTextLc: 'user' },
    { id: UserType.Agent, text: 'Agent', searchTextLc: 'agent' },
    { id: UserType.Superuser, text: 'Admin', searchTextLc: 'admin' },
    ...(adminIsOwner ? [{ id: UserType.Owner, text: 'Owner', searchTextLc: 'owner' }] : []),
  ];

  // Default ('') = this server; the rest are the configured API instances by base URL.
  const serverOptions: DropDownSelectorValue[] = [
    { id: '', text: 'This server (default)', searchTextLc: 'this default server' },
    ...apiServers.map(s => ({ id: s.url, text: s.name, searchTextLc: s.name.toLowerCase() })),
  ];

  const userTypeLabels: Record<string, string> = {
    [UserType.Owner]: 'Owner',
    [UserType.Superuser]: 'Superuser',
    [UserType.Agent]: 'Agent',
    [UserType.User]: 'User',
  };

  // Admin annotation for a recent-user row from its auth summary. undefined summary => still loading.
  const recentAdminLabel = (summary?: RestAccountSummary): string => {
    if (!summary) return '…';
    if (summary.userType == 'super') return 'Super';
    const capType = summary.capabilities ? getUserType(summary.capabilities) : UserType.User;
    return capType != UserType.User ? (userTypeLabels[capType] || 'Admin') : 'No';
  };

  const downreadMembership = account?.memberships.find(m => m.appId == 'downread');
  const originalCapabilitiesUserType = downreadMembership ? getUserType(downreadMembership.capabilities) : UserType.User;
  const hasChanges = user && (
    user.userStatus != userStatus ||
    user.nickName != nickName ||
    user.userPlan != userPlan
  ) || (account && (
    account.userType != userType ||
    account.status != accountStatus
  )) || (!!downreadMembership && capabilitiesUserType != originalCapabilitiesUserType);

  // Mirror AccountPage's self-delete guard: a privileged target (any non-User capability, or an
  // account-level super user) can't be hard-deleted directly — the admin must demote it first
  // (set Capabilities to User / Account User Type to user and Save), then delete. Based on the
  // SAVED account/membership, not the unsaved dropdown selections.
  const targetPrivileged = !!account && (originalCapabilitiesUserType != UserType.User || account.userType == 'super');

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
    <MainLayout
      selectedFeed="admin-users"
      right={({ onExpand }) => (
        <FlowForm main={true} className='full-margin'>
          <style>{`
            .user-view-container {
              max-width: 700px;
              margin: 0 auto;
            }
            .user-view-container h2 {
              color: var(--gray-200);
            }
            .user-form-field {
              margin-bottom: 24px;
            }
            .user-form-label {
              display: block;
              font-weight: 600;
              margin-bottom: 8px;
              color: var(--gray-350);
              font-size: 0.95rem;
            }
            .user-form-note {
              background: var(--gray-975);
              border-left: 4px solid var(--gray-500);
              padding: 12px 16px;
              margin-top: 24px;
              color: var(--gray-350);
              font-size: 0.9em;
              border-radius: 4px;
            }
            .user-loading,
            .user-error {
              max-width: 700px;
              margin: 0 auto;
              padding: 60px 24px;
              text-align: center;
            }
            .user-error {
              color: var(--error-color);
            }
            .user-loading {
              color: var(--gray-500);
            }
            .user-dropdown-field .DropDownSelector {
              width: auto !important;
              min-width: 200px;
            }
            /* Consistent height for inputs so they line up with the (taller) action buttons. */
            .user-view-container input.labelledInput {
              padding: 9px 12px;
            }
            /* A horizontal row of an input (grows) + one or more action buttons of equal height. */
            .user-action-row {
              display: flex;
              gap: 10px;
              align-items: stretch;
              flex-wrap: wrap;
            }
            .user-action-row > .user-action-field {
              flex: 1 1 240px;
              min-width: 0;
            }
            .user-action-row .textButton {
              align-self: stretch;
              padding-top: 0;
              padding-bottom: 0;
              min-width: 116px;
              white-space: nowrap;
            }
            .user-create-card {
              background: var(--gray-975);
              border: 1px solid var(--gray-925);
              border-radius: 8px;
              padding: 20px;
              margin-top: 16px;
            }
            .user-create-card .user-form-field:last-child {
              margin-bottom: 0;
            }
            .user-data-grid {
              display: grid;
              grid-template-columns: 150px 1fr;
              gap: 12px;
              background: var(--gray-975);
              padding: 16px;
              border-radius: 4px;
              margin-top: 16px;
            }
            .user-data-label {
              font-weight: 600;
              color: var(--gray-350);
            }
            .user-data-value {
              color: var(--gray-200);
              word-break: break-all;
            }
            .recent-users-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 12px;
              font-size: 0.9em;
            }
            .recent-users-table th {
              text-align: left;
              padding: 8px 10px;
              color: var(--gray-350);
              font-weight: 600;
              border-bottom: 1px solid var(--gray-925);
              white-space: nowrap;
            }
            .recent-users-table td {
              padding: 8px 10px;
              color: var(--gray-200);
              border-bottom: 1px solid var(--gray-975);
              white-space: nowrap;
            }
            .recent-users-table tr.clickable {
              cursor: pointer;
            }
            .recent-users-table tr.clickable:hover td {
              background: var(--gray-975);
            }
            .recent-users-table .col-admin-yes {
              color: var(--gray-100);
              font-weight: 600;
            }
            .recent-users-table .col-muted {
              color: var(--gray-500);
            }
            .recent-users-table .col-orphan {
              color: var(--error-color);
              font-weight: 600;
            }
            .recent-users-scroll {
              overflow-x: auto;
            }
          `}</style>

          <div className="user-view-container">
            <AdminHeader title="View User" onExpand={onExpand} />

            {!showCreate && (
              <ButtonBar size="small">
                {user && (
                  <TextButton
                    label="Save"
                    icon={saveIcon}
                    onClick={handleSave}
                    focussable="flow"
                    type="major"
                    busy={saving}
                    enabled={saveError ? 'error' : (hasChanges ? 'enabled' : 'disabled')}
                  />
                )}
                <TextButton
                  label="Manage Tags"
                  icon={tagsIcon}
                  onClick={() => goTo(routerCtx, '/a/admin/tags')}
                  focussable="flow"
                />
              </ButtonBar>
            )}

            {saveError && (
              <div className="user-form-note" style={{ background: '#fee', borderLeft: '4px solid var(--error-color)', color: 'var(--error-color)', marginTop: '16px' }}>
                {saveError}
              </div>
            )}

            <div style={{ marginTop: '32px' }}>
              {deletedMessage && (
                <div className="user-form-note" style={{ background: '#e6f4ea', borderLeft: '4px solid #34a853', color: '#1e6e35', marginBottom: '16px' }}>
                  {deletedMessage}
                </div>
              )}

              {/* While creating a new user, every other function is hidden so the admin focuses on the
                  create form (moved to the bottom of the page). */}
              {!showCreate && (<>
              <div className="user-form-field">
                <label className="user-form-label">
                  Search by Email or Account ID
                </label>
                <div className="user-action-row">
                  <div className="user-action-field">
                    <Input
                      value={searchQuery}
                      onInput={setSearchQuery}
                      placeholder="user@example.com or account-id"
                      layout="singleCol"
                      onEnter={handleEnter}
                    />
                  </div>
                  <TextButton
                    label="Search"
                    icon={searchIcon}
                    onClick={() => handleSearch()}
                    focussable="flow"
                    type="major"
                    busy={loading}
                  />
                </div>
              </div>

              {error && (
                <div className="user-form-note" style={{ background: '#fee', borderLeft: '4px solid var(--error-color)', color: 'var(--error-color)' }}>
                  {error}
                </div>
              )}

              {/* Recent users. Lists the last created users on the selected API instance (newest
                  first) so untagged test accounts can be spotted and opened for deletion. Hidden once
                  a specific account is displayed. */}
              {!account && (
              <div className="user-form-field" style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--gray-925)' }}>
                <label className="user-form-label">Recent Users</label>
                <div style={{ fontSize: '0.85em', color: 'var(--gray-500)', marginBottom: '8px' }}>
                  The last 10 users created on the selected server, newest first. Mainly for finding
                  and deleting untagged test users — click a row to open it for inspection or deletion.
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {apiServers.length > 0 && (
                    <div className="user-dropdown-field">
                      <DropDownSelector
                        selections={serverOptions}
                        value={recentServerUrl}
                        onSelect={(v: DropDownSelectorValue) => setRecentServerUrl(v.id as string)}
                        focussable="flow"
                      />
                    </div>
                  )}
                  <TextButton
                    label="Load recent users"
                    icon={searchIcon}
                    onClick={handleLoadRecent}
                    focussable="flow"
                    type="major"
                    busy={recentLoading}
                  />
                </div>

                {recentError && (
                  <div className="user-form-note" style={{ background: '#fee', borderLeft: '4px solid var(--error-color)', color: 'var(--error-color)' }}>
                    {recentError}
                  </div>
                )}

                {recentActionError && (
                  <div className="user-form-note" style={{ background: '#fee', borderLeft: '4px solid var(--error-color)', color: 'var(--error-color)' }}>
                    {recentActionError}
                  </div>
                )}

                {recentUsers && (recentUsers.length ? (
                  <div className="recent-users-scroll">
                    <table className="recent-users-table">
                      <thead>
                        <tr>
                          <th>Created</th>
                          <th>Admin</th>
                          <th>Tag</th>
                          <th>Last Login</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentUsers.map(u => {
                          const summary = recentSummaries[u.accountId];
                          // No summary AFTER a successful annotation => the auth account is gone.
                          const orphaned = recentSummariesReady && !summary;
                          const adminLabel = orphaned ? 'No account' : recentAdminLabel(summary);
                          const isAdmin = !orphaned && adminLabel != 'No' && adminLabel != '…';
                          const lastLogin = orphaned ? '—'
                            : summary ? (summary.lastLogin ? new Date(summary.lastLogin).toLocaleString() : 'Never') : '…';
                          return (
                            <tr key={u.id} className={orphaned ? '' : 'clickable'} onClick={orphaned ? undefined : () => handleOpenRecent(u.accountId)}>
                              <td>{new Date(u.createdAt).toLocaleString()}</td>
                              <td className={orphaned ? 'col-orphan' : isAdmin ? 'col-admin-yes' : 'col-muted'}>{adminLabel}</td>
                              <td className={u.tag ? '' : 'col-muted'}>{u.tag || '(none)'}</td>
                              <td className="col-muted">{lastLogin}</td>
                              <td className={u.userStatus == 'active' ? 'col-muted' : ''}>{u.userStatus}</td>
                              <td>
                                {orphaned ? (
                                  confirmOrphanId == u.id ? (
                                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '6px', alignItems: 'stretch' }}>
                                      <TextButton
                                        label="Confirm"
                                        onClick={() => handleDeleteOrphan(u.id)}
                                        focussable="flow"
                                        type="major"
                                        size="small"
                                        busy={orphanBusyId == u.id}
                                        enabled={orphanBusyId ? 'disabled' : 'enabled'}
                                      />
                                      <TextButton
                                        label="Cancel"
                                        onClick={() => setConfirmOrphanId(undefined)}
                                        focussable="flow"
                                        size="small"
                                        enabled={orphanBusyId ? 'disabled' : 'enabled'}
                                      />
                                    </span>
                                  ) : (
                                    <TextButton
                                      label="Delete"
                                      onClick={() => { setRecentActionError(undefined); setConfirmOrphanId(u.id); }}
                                      focussable="flow"
                                      type="major"
                                      size="small"
                                      enabled={orphanBusyId ? 'disabled' : 'enabled'}
                                    />
                                  )
                                ) : (
                                  <TextButton
                                    label="View"
                                    onClick={() => handleOpenRecent(u.accountId)}
                                    focussable="flow"
                                    type="major"
                                    size="small"
                                  />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ marginTop: '12px', color: 'var(--gray-500)', fontSize: '0.9em' }}>
                    No users found on this server.
                  </div>
                ))}
              </div>
              )}

              {/* Feed → creator lookup. Public-feed creators are tracked per API instance, so the
                  admin selects which server to query (and tries others if not found on the first).
                  Hidden once an account is displayed — it's unrelated to viewing a specific user. */}
              {!account && (
              <div className="user-form-field" style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--gray-925)' }}>
                <label className="user-form-label">Find Feed Creator</label>
                <div style={{ fontSize: '0.85em', color: 'var(--gray-500)', marginBottom: '8px' }}>
                  Enter a feed ID to find who created it. Public-feed creators are stored on the
                  creator's own API server, so pick a server to query — try the others if not found.
                </div>
                <div className="user-action-row">
                  <div className="user-action-field">
                    <Input
                      value={feedCreatorQuery}
                      onInput={setFeedCreatorQuery}
                      placeholder="feed id (e.g. f12ab or fppXXXX)"
                      layout="singleCol"
                      onEnter={() => handleFindCreator()}
                    />
                  </div>
                  {apiServers.length > 0 && (
                    <div className="user-dropdown-field">
                      <DropDownSelector
                        selections={serverOptions}
                        value={selectedServerUrl}
                        onSelect={(v: DropDownSelectorValue) => setSelectedServerUrl(v.id as string)}
                        focussable="flow"
                      />
                    </div>
                  )}
                  <TextButton
                    label="Find"
                    icon={searchIcon}
                    onClick={() => handleFindCreator()}
                    focussable="flow"
                    type="major"
                    busy={creatorLoading}
                  />
                </div>

                {creatorError && (
                  <div className="user-form-note" style={{ background: '#fee', borderLeft: '4px solid var(--error-color)', color: 'var(--error-color)' }}>
                    {creatorError}
                  </div>
                )}

                {creatorResult && (
                  <div className="user-data-grid" style={{ marginTop: '12px' }}>
                    <div className="user-data-label">Feed ID:</div>
                    <div className="user-data-value">{creatorResult.feedId}</div>

                    {creatorResult.source == 'none' ? (
                      <>
                        <div className="user-data-label">Result:</div>
                        <div className="user-data-value">
                          No creator found on this server.{apiServers.length > 1 ? ' Select another server above and try again.' : ''}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="user-data-label">Creator User ID:</div>
                        <div className="user-data-value">{creatorResult.creatorUserId}</div>

                        <div className="user-data-label">Source:</div>
                        <div className="user-data-value">
                          {creatorResult.source == 'feed' ? 'Feed record (private/newsletter)' : 'User createdFeeds'}
                        </div>

                        {creatorResult.user ? (
                          <>
                            <div className="user-data-label">Account ID:</div>
                            <div className="user-data-value">{creatorResult.user.accountId}</div>

                            <div className="user-data-label">Nick Name:</div>
                            <div className="user-data-value">{creatorResult.user.nickName}</div>

                            <div className="user-data-label">Plan:</div>
                            <div className="user-data-value">{creatorResult.user.userPlan}</div>

                            <div className="user-data-label"></div>
                            <div className="user-data-value">
                              <TextButton
                                label="Open user record"
                                onClick={() => { setSearchQuery(creatorResult.user!.accountId); handleSearch(creatorResult.user!.accountId); }}
                                focussable="flow"
                                type="major"
                                size="small"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="user-data-label">Note:</div>
                            <div className="user-data-value">
                              This creator's full record lives on a different API server. Select that
                              server above and look up again to load their details.
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              )}

              {account && (() => {
                const membership = account.memberships.find(m => m.appId == 'downread');
                return (
                <div className="user-form-field" style={{ marginTop: '32px' }}>
                  <label className="user-form-label">
                    Account Information
                  </label>
                  <div className="user-data-grid">
                    <div className="user-data-label">Account ID:</div>
                    <div className="user-data-value">{account.id}</div>

                    <div className="user-data-label">Email:</div>
                    <div className="user-data-value">{account.email || '(none)'}</div>

                    <div className="user-data-label">Jurisdiction:</div>
                    <div className="user-data-value">{account.jurisdiction}</div>

                    <div className="user-data-label">Sign-in Method:</div>
                    <div className="user-data-value">
                      {[account.hasGoogle && 'Google', account.hasApple && 'Apple', account.hasPassword && 'Password'].filter(Boolean).join(' + ')
                        || 'None (no password, Google or Apple linked)'}
                    </div>

                    {user && (<>
                      <div className="user-data-label">User ID:</div>
                      <div className="user-data-value">{user.id}</div>
                    </>)}

                    {membership && (<>
                      <div className="user-data-label">Active:</div>
                      <div className="user-data-value">{membership.active ? 'Yes' : 'No'}</div>

                      <div className="user-data-label">Capabilities:</div>
                      <div className="user-data-value">{userTypeLabels[getUserType(membership.capabilities)] || membership.capabilities}</div>

                      {membership.instance && (<>
                        <div className="user-data-label">Instance:</div>
                        <div className="user-data-value">{membership.instance}</div>
                      </>)}
                    </>)}
                  </div>

                  <div className="user-form-field user-dropdown-field" style={{ marginTop: '16px' }}>
                    <label className="user-form-label">
                      Account Status
                    </label>
                    <DropDownSelector
                      selections={accountStatusOptions}
                      value={accountStatus}
                      onSelect={(v: DropDownSelectorValue) => setAccountStatus(v.id as AccountStatus)}
                      focussable="flow"
                    />
                  </div>

                  <div className="user-form-field user-dropdown-field">
                    <label className="user-form-label">
                      Account User Type <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: '0.85em' }}>(account-level, not app-layer)</span>
                    </label>
                    <DropDownSelector
                      selections={userTypeOptions}
                      value={userType}
                      onSelect={(v: DropDownSelectorValue) => setUserType(v.id as 'user' | 'super')}
                      focussable="flow"
                    />
                  </div>

                  <div className="user-form-field user-dropdown-field">
                    <label className="user-form-label">
                      Downread Capabilities <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: '0.85em' }}>(app-layer permissions)</span>
                    </label>
                    <DropDownSelector
                      selections={capabilitiesOptions}
                      value={capabilitiesUserType}
                      onSelect={(v: DropDownSelectorValue) => setCapabilitiesUserType(v.id as UserType)}
                      focussable="flow"
                    />
                  </div>

                  {/* Stale-account cleanup: when no Downread membership exists, the account is
                      orphaned in the auth service. Allow admins to hard-delete it. */}
                  {!membership && (
                    <div style={{ marginTop: '24px', padding: '16px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 600, marginBottom: '8px', color: '#856404' }}>
                        Orphaned auth account
                      </div>
                      <div style={{ marginBottom: '12px', color: '#856404', fontSize: '0.9em' }}>
                        This auth account has no Downread membership. It can safely be hard-deleted from the auth service to clean up the database.
                      </div>
                      {deleteError && (
                        <div style={{ marginBottom: '12px', color: 'var(--error-color)', fontSize: '0.9em' }}>
                          {deleteError}
                        </div>
                      )}
                      {!confirmDelete ? (
                        <TextButton
                          label="Delete auth account…"
                          onClick={() => { setDeleteError(undefined); setConfirmDelete(true); }}
                          focussable="flow"
                          type="major"
                          enabled={deleteBusy ? 'disabled' : 'enabled'}
                        />
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ color: '#856404', fontWeight: 600 }}>This is irreversible. Proceed?</span>
                          <TextButton
                            label="Yes, delete"
                            onClick={handleDeleteAuthAccount}
                            focussable="flow"
                            type="major"
                            busy={deleteBusy}
                            enabled={deleteBusy ? 'disabled' : 'enabled'}
                          />
                          <TextButton
                            label="Cancel"
                            onClick={() => setConfirmDelete(false)}
                            focussable="flow"
                            enabled={deleteBusy ? 'disabled' : 'enabled'}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );})()}

              {confirmElevation && (
                <div style={{ padding: '20px', marginBottom: '16px', background: '#fff3cd', color: '#856404', borderRadius: '4px', border: '2px solid #ffc107' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '8px' }}>
                    ⚠ Confirm Permission Elevation
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    You are about to grant <strong>{confirmElevation.label}</strong> permissions to this user.
                    This gives them elevated access to the system. Are you sure?
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <TextButton
                      label={`Yes, make ${confirmElevation.label}`}
                      onClick={() => handleSave(confirmElevation.userType)}
                      focussable="flow"
                      type="major"
                      busy={saving}
                    />
                    <TextButton
                      label="Cancel"
                      onClick={() => setConfirmElevation(null)}
                      focussable="flow"
                    />
                  </div>
                </div>
              )}

              {user && (
                <>
                  <div className="user-form-field">
                    <label className="user-form-label">
                      User Information
                    </label>
                    <div className="user-data-grid">
                      <div className="user-data-label">User ID:</div>
                      <div className="user-data-value">{user.id}</div>

                      <div className="user-data-label">Account ID:</div>
                      <div className="user-data-value">{user.accountId}</div>

                      <div className="user-data-label">Number of Feeds:</div>
                      <div className="user-data-value">{user.feeds.length}</div>

                      <div className="user-data-label">Last Update:</div>
                      <div className="user-data-value">{new Date(user.lastUpdate).toLocaleString()}</div>

                      <div className="user-data-label">Paid Until:</div>
                      <div className="user-data-value">{user.userPaidUntil ? new Date(user.userPaidUntil).toLocaleString() : '(none)'}</div>

                      <div className="user-data-label">Created At:</div>
                      <div className="user-data-value">{new Date(user.createdAt).toLocaleString()}</div>
                    </div>
                  </div>

                  <div className="user-form-field">
                    <label className="user-form-label">
                      Nick Name
                    </label>
                    <Input
                      value={nickName}
                      onInput={setNickName}
                      placeholder="Nick name"
                      layout="singleCol"
                    />
                  </div>

                  <div className="user-form-field user-dropdown-field">
                    <label className="user-form-label">
                      User Plan
                    </label>
                    <DropDownSelector
                      selections={planOptions}
                      value={userPlan}
                      onSelect={(v: DropDownSelectorValue) => setUserPlan(v.id as UserPlan)}
                      focussable="flow"
                    />
                  </div>

                  <div className="user-form-field user-dropdown-field">
                    <label className="user-form-label">
                      User Status
                    </label>
                    <DropDownSelector
                      selections={statusOptions}
                      value={userStatus}
                      onSelect={(v: DropDownSelectorValue) => setUserStatus(v.id as 'active' | 'suspended' | 'deleted')}
                      focussable="flow"
                    />
                  </div>

                  {/* Ban / unban this identity. A ban suspends the account, ends its current
                      session, and writes a permanent tombstone so the email can't re-register. */}
                  <div className="user-form-field" style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--gray-925)' }}>
                    <label className="user-form-label" style={{ color: 'var(--error-color)' }}>Ban</label>
                    {banError && (
                      <div style={{ marginBottom: '12px', color: 'var(--error-color)', fontSize: '0.9em' }}>{banError}</div>
                    )}
                    {isBanned ? (
                      <>
                        <div style={{ marginBottom: '12px', color: 'var(--gray-350)', fontSize: '0.9em' }}>
                          This identity is <strong style={{ color: 'var(--error-color)' }}>banned</strong> ({account?.banStatus}) — it can't log in or re-register.
                        </div>
                        <TextButton
                          label="Lift ban"
                          onClick={handleLiftBan}
                          focussable="flow"
                          type="minor"
                          busy={banBusy}
                          enabled={banBusy ? 'disabled' : 'enabled'}
                        />
                      </>
                    ) : (
                      <>
                        <div style={{ marginBottom: '12px', color: 'var(--gray-500)', fontSize: '0.9em' }}>
                          Suspends the account, ends its current session, and blocks this email from logging in or re-registering. Reversible.
                        </div>
                        {!confirmBan ? (
                          <TextButton
                            label="Ban user…"
                            onClick={() => { setBanError(undefined); setConfirmBan(true); }}
                            focussable="flow"
                            type="major"
                            enabled={banBusy ? 'disabled' : 'enabled'}
                          />
                        ) : (
                          <>
                            <div className="user-form-field" style={{ maxWidth: '400px' }}>
                              <Input
                                value={banReason}
                                onInput={setBanReason}
                                placeholder="Reason (optional)"
                                layout="singleCol"
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ color: 'var(--error-color)', fontWeight: 600 }}>Permanently ban this user?</span>
                              <TextButton
                                label="Yes, ban"
                                onClick={handleBan}
                                focussable="flow"
                                type="major"
                                busy={banBusy}
                                enabled={banBusy ? 'disabled' : 'enabled'}
                              />
                              <TextButton
                                label="Cancel"
                                onClick={() => { setConfirmBan(false); setBanReason(''); }}
                                focussable="flow"
                                enabled={banBusy ? 'disabled' : 'enabled'}
                              />
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {/* Hard-delete this user — admin equivalent of the self-delete on the Account page.
                      Tears down the target's feeds, user record and auth account server-side. */}
                  <div className="user-form-field" style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--gray-925)' }}>
                    <label className="user-form-label" style={{ color: 'var(--error-color)' }}>Delete User</label>
                    <div style={{ marginBottom: '12px', color: 'var(--gray-500)', fontSize: '0.9em' }}>
                      Permanently delete this user: unsubscribe them from every feed, then erase their profile, read
                      history, starred and read-later lists, and their auth account. This cannot be undone.
                    </div>
                    {targetPrivileged ? (
                      <div style={{ color: 'var(--gray-500)', fontSize: '0.9em', fontStyle: 'italic' }}>
                        This is a privileged account (Owner, Admin or Agent, or an account-level super user). Demote it
                        first — set Downread Capabilities to User and Account User Type to user above, Save — then delete.
                      </div>
                    ) : (<>
                      {userDeleteError && (
                        <div style={{ marginBottom: '12px', color: 'var(--error-color)', fontSize: '0.9em' }}>
                          {userDeleteError}
                        </div>
                      )}
                      {!confirmUserDelete ? (
                        <TextButton
                          label="Delete user…"
                          onClick={() => { setUserDeleteError(undefined); setConfirmUserDelete(true); }}
                          focussable="flow"
                          type="major"
                          enabled={userDeleteBusy ? 'disabled' : 'enabled'}
                        />
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ color: 'var(--error-color)', fontWeight: 600 }}>Permanently delete this user? This is irreversible.</span>
                          <TextButton
                            label="Yes, delete user"
                            onClick={handleDeleteUser}
                            focussable="flow"
                            type="major"
                            busy={userDeleteBusy}
                            enabled={userDeleteBusy ? 'disabled' : 'enabled'}
                          />
                          <TextButton
                            label="Cancel"
                            onClick={() => setConfirmUserDelete(false)}
                            focussable="flow"
                            enabled={userDeleteBusy ? 'disabled' : 'enabled'}
                          />
                        </div>
                      )}
                    </>)}
                  </div>
                </>
              )}
              </>)}

              {/* Create a new user (bottom of the page): a confirmed auth account (email + password +
                  jurisdiction) plus its Downread user record, created on the jurisdiction's API
                  instance. While the form is open, the !showCreate gate above hides every other
                  function so the admin focuses on creation; on success the new user is shown for editing. */}
              <div className="user-form-field" style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--gray-925)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="user-form-label" style={{ marginBottom: 0 }}>Create New User</label>
                  {!showCreate && (
                    <TextButton
                      label="New user…"
                      icon={personAddIcon}
                      onClick={() => { setCreateError(undefined); setShowCreate(true); }}
                      focussable="flow"
                      size="small"
                    />
                  )}
                </div>

                {showCreate && (
                  <div className="user-create-card">
                    <div style={{ fontSize: '0.85em', color: 'var(--gray-500)', marginBottom: '20px' }}>
                      Creates a confirmed account (no confirmation email is sent). The user is placed in
                      the selected data-residency jurisdiction — this cannot be changed later.
                    </div>

                    <div className="user-form-field">
                      <label className="user-form-label">Email</label>
                      <Input
                        value={createEmail}
                        onInput={setCreateEmail}
                        placeholder="user@example.com"
                        layout="singleCol"
                      />
                    </div>

                    <div className="user-form-field">
                      <label className="user-form-label">Password</label>
                      <PasswordInput
                        value={createPassword}
                        onInput={setCreatePassword}
                        placeholder="Password"
                        layout="singleCol"
                      />
                    </div>

                    <div className="user-form-field user-dropdown-field">
                      <label className="user-form-label">Jurisdiction</label>
                      <DropDownSelector
                        selections={jurisdictionOptions}
                        value={createJurisdiction}
                        onSelect={(v: DropDownSelectorValue) => setCreateJurisdiction(v.id as Jurisdiction)}
                        focussable="flow"
                      />
                    </div>

                    <div className="user-form-field">
                      <label className="user-form-label">
                        Tag <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: '0.85em' }}>(optional — labels test/batch accounts for later cleanup)</span>
                      </label>
                      <Input
                        value={createTag}
                        onInput={setCreateTag}
                        placeholder="e.g. batch-2026-07"
                        layout="singleCol"
                      />
                    </div>

                    {createError && (
                      <div className="user-form-note" style={{ background: '#fee', borderLeft: '4px solid var(--error-color)', color: 'var(--error-color)' }}>
                        {createError}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      <TextButton
                        label="Create User"
                        icon={personAddIcon}
                        onClick={handleCreate}
                        focussable="flow"
                        type="major"
                        busy={creating}
                        enabled={creating ? 'disabled' : 'enabled'}
                      />
                      <TextButton
                        label="Cancel"
                        onClick={() => { setShowCreate(false); setCreateError(undefined); }}
                        focussable="flow"
                        type="minor"
                        enabled={creating ? 'disabled' : 'enabled'}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </FlowForm>
      )}
      startWithLeftCollapsed={false}
    />
    </FlowFormLeaveContext.Provider>
  );
};

export default AdminViewUserPage;
